<template>
  <SidebarInnerWrapper :placement="placement" class="chat-container">
    <FriedMessage v-if="error" :type="MessageType.Error" :title="t('dsr_failed_to_load_chat')" />
    <FriedSpinner v-else-if="!loaded" class="chat-loader" />
    <div v-else class="chat-content-container">
      <ChatHeader
        :conversation="conversation"
        :creating-new-conversation="creatingNewConversation"
        :chat-participants="chatParticipants"
        :owner-company-name="ownerCompanyName"
        :open-conversation-payload="openConversationPayload"
        :participants="allParticipants"
        :participant-id="participantId"
        :user="user"
        :previous-room-owners-ids="previousRoomOwnersIds"
        published-view
        @update-participants="handleNewConversationParticipants"
        @show-conversation-list="$emit('show-conversation-list')"
      />
      <DeleteChatMessageModal
        v-if="showDeleteMessageModal"
        :user="user"
        :participants="participants"
        :message="messageToBeDeleted"
        :messages="conversation.messages"
        :owner="owner"
        @close="showDeleteMessageModal = false"
        @confirm="handleDelete"
      />
      <ChatContent
        v-if="!creatingNewConversation"
        :user="user"
        :participants="allParticipants"
        :conversation="conversation"
        :owner="owner"
        :last-reads="lastReads"
        :typing="typing"
        :previous-room-owners-ids="previousRoomOwnersIds"
        @read="handleRead"
        @delete="handleDelete"
        @reply="handleReply"
        @retry-send="retrySend($event.id)"
      />
      <ChatActions
        v-if="canSendMessages"
        :class="['chat-actions-container', { extended: !!replyMessage }]"
        :reply-message="replyMessage"
        :participants="allParticipants"
        :user="user"
        :conversation="conversation"
        :creating-new-conversation="creatingNewConversation"
        :draft-storage-key="draftStorageKey"
        @send="handleSend"
        @clear-reply="handleClearReply"
        @typing="handleTyping"
      />
    </div>
  </SidebarInnerWrapper>
</template>

<script lang="ts">
import type { PropType, Ref } from 'vue';
import { computed, ref, defineComponent, onBeforeUnmount } from 'vue';
import { storeToRefs } from 'pinia';
import {
  SidebarInnerWrapper,
  SidebarPlacement,
  ChatContent,
  ChatActions,
  ChatHeader,
  DeleteChatMessageModal,
  ChatHelper,
  ConversationType,
} from '@getaccept/dsr-shared-new';
import type {
  ChatParticipant,
  Message,
  Participant,
  OpenConversationPayload,
} from '@getaccept/dsr-shared-new';
import { MessageType } from '@getaccept/fried-tofu';
import { useI18n } from 'vue-i18n';
import { useChatPublishedStore } from '../store/chat-published.store';
import { useParticipantsStore } from '../../store/participants.store';

export default defineComponent({
  name: 'ChatWrapper',
  components: {
    ChatContent,
    ChatActions,
    SidebarInnerWrapper,
    ChatHeader,
    DeleteChatMessageModal,
  },
  props: {
    participantId: {
      required: true,
      type: String,
    },
    placement: {
      type: String as PropType<SidebarPlacement>,
      default: () => SidebarPlacement.Right,
    },
    loaded: {
      type: Boolean,
    },
    creatingNewConversation: {
      type: Boolean,
    },
    chatParticipants: {
      type: Array as PropType<ChatParticipant[]>,
      default: () => [],
    },
    allParticipants: {
      type: Array as PropType<Participant[]>,
      default: () => [],
    },
    ownerCompanyName: {
      type: String,
      default: '',
    },
    openConversationPayload: {
      type: Object as PropType<OpenConversationPayload>,
      default: null,
    },
    draftStorageKey: {
      type: String,
      required: true,
    },
    previousRoomOwnersIds: {
      type: Array as PropType<string[]>,
      default: () => [],
    },
  },
  setup(props, { emit }) {
    const { t } = useI18n();
    const participantsStore = useParticipantsStore();
    const chatStore = useChatPublishedStore();
    const {
      conversation,
      loadingConversationDetails,
      loadingConversationList,
      error,
      typing,
      replyMessage,
    } = storeToRefs(chatStore);
    const { participants, senders, owner: user } = storeToRefs(participantsStore);

    const owner: ChatParticipant = {
      participantId: props.participantId,
    };

    const lastReads = computed(() =>
      ChatHelper.getConversationLastRead(
        conversation.value,
        participants.value,
        senders.value,
        owner
      )
    );

    const handleSend = async (text: string) => {
      if (props.creatingNewConversation) {
        await chatStore.createConversation(props.participantId, text);
        emit('conversation-created');
        return;
      }
      chatStore.createMessage(text, props.participantId);
      handleClearReply();
    };

    const handleRead = (messageId: string) => chatStore.setMessageReadBy(messageId);
    const handleTyping = (isTyping: boolean) => chatStore.setTypingStatus(isTyping);

    const showDeleteMessageModal = ref(false);
    const messageToBeDeleted: Ref<Message> = ref();
    const handleDelete = (messageId: string) => {
      if (!showDeleteMessageModal.value) {
        messageToBeDeleted.value = conversation.value.messages.find(
          message => message.id === messageId
        );
        showDeleteMessageModal.value = true;
        return;
      }
      chatStore.deleteMessage(messageId);
      showDeleteMessageModal.value = false;
      messageToBeDeleted.value = null;
    };

    const handleReply = (message: Message) => {
      chatStore.setReplyMessage(message);
    };

    const handleClearReply = (): void => {
      chatStore.setReplyMessage(undefined);
    };

    const canSendMessages = computed(() => {
      if (props.previousRoomOwnersIds.length === 0 || !conversation.value) {
        return true;
      }

      return (
        conversation.value.type === ConversationType.General ||
        !props.previousRoomOwnersIds.some(id =>
          ChatHelper.isParticipantInConversation(conversation.value, id)
        )
      );
    });

    onBeforeUnmount(() => {
      handleClearReply();
    });

    return {
      MessageType,
      conversation,
      loadingConversationDetails,
      loadingConversationList,
      error,
      typing,
      replyMessage,
      participants,
      user,
      handleSend,
      owner,
      handleRead,
      lastReads,
      handleTyping,
      handleDelete,
      handleReply,
      handleClearReply,
      handleNewConversationParticipants: chatStore.handleNewConversationParticipants,
      showDeleteMessageModal,
      messageToBeDeleted,
      retrySend: chatStore.retrySend,
      t,
      canSendMessages,
    };
  },
});
</script>

<style lang="scss" scoped>
@import '@getaccept/dsr-shared-new/src/scss/chat-wrapper';
</style>
